import { ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import {
  AbstractControl,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { emailValidator } from '@recruitee/common';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import {
  ButtonComponent,
  FormFieldComponent,
  GroupComponent,
  IconButtonComponent,
  IconComponent,
  InputComponent,
  LabelComponent,
  SelectComponent,
  SelectOption,
  WindowRef,
} from '@recruitee/user-interface';

@Component({
  standalone: true,
  selector: 'rt-invitation-form',
  templateUrl: 'invitation-form.component.html',
  styleUrls: ['./invitation-form.component.less'],
  imports: [
    ButtonComponent,
    FormFieldComponent,
    GroupComponent,
    IconButtonComponent,
    IconComponent,
    InputComponent,
    LabelComponent,
    SelectComponent,
    TranslatePipe,
    ReactiveFormsModule,
  ],
})
export class InvitationFormComponent {
  @Input() public roles: SelectOption[];
  @Input() public currentAdminEmails: string[] = [];
  @Input() public invitedEmails: string[] = [];

  @Output()
  public valueChanges: EventEmitter<{
    valid: boolean;
    invitations: Array<{ email: string; roleId: number }>;
  }> = new EventEmitter();

  public invitationForm: UntypedFormArray;

  private ngOnDestroy$ = new Subject<void>();
  private readonly pendingInvitesUrl: string;

  constructor(
    private fb: UntypedFormBuilder,
    protected cdRef: ChangeDetectorRef,
    private i18n: I18nService,
    private windowRef: WindowRef,
  ) {
    this.pendingInvitesUrl = this.buildPendingInvitesUrl();
  }

  public ngOnInit(): void {
    this.invitationForm = this.fb.array([this.createFormGroup()]);
    this.invitationForm.valueChanges
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(value =>
        this.valueChanges.emit({ invitations: value, valid: this.invitationForm.valid }),
      );
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public trackByError(index: number, error: string): string {
    return error;
  }

  public getErrorMessages(control: AbstractControl): string[] {
    if (!control.errors || (control.errors && !Object.keys(control.errors).length)) {
      return [];
    }
    return Object.keys(control.errors)
      .map(key => {
        switch (key) {
          case 'userExists':
            return this.i18n.translate('app.invitation_form.user_exists');
          case 'alreadyInvited':
            return this.i18n.translate('app.invitation_form.already_invited_v2_html', {
              pendingInvitesUrl: this.pendingInvitesUrl,
            });
          default:
            return '';
        }
      })
      .filter(message => !!message);
  }

  public addInvitationForm(): void {
    this.invitationForm.push(this.createFormGroup());
  }

  public removeInvitationForm(index: number): void {
    this.invitationForm.removeAt(index);
  }

  public trackByControl(index: number, control: AbstractControl): AbstractControl {
    return control;
  }

  public userExistsValidator(control: AbstractControl): { userExists: true } | null {
    return control.value && this.currentAdminEmails.includes(control.value.toLowerCase())
      ? { userExists: true }
      : null;
  }

  public alreadyInvitedValidator(control: AbstractControl): { alreadyInvited: true } | null {
    return control.value && this.invitedEmails.includes(control.value.toLowerCase())
      ? { alreadyInvited: true }
      : null;
  }

  private createFormGroup(): UntypedFormGroup {
    return this.fb.group({
      email: [
        '',
        [
          Validators.required,
          emailValidator,
          this.userExistsValidator.bind(this),
          this.alreadyInvitedValidator.bind(this),
        ],
      ],
      roleId: ['', [Validators.required]],
    });
  }

  private buildPendingInvitesUrl(): string {
    const location = this.windowRef.nativeWindow.location;
    return location.protocol + '//' + location.host + '/#/settings/members/invites?page=1&limit=15';
  }
}
